import { FieldGrid, FieldGridPool, FieldGridBounds } from './FieldGrid';

const DEFAULT_MEMORY_BUDGET = 64 * 1024 * 1024; // 64MB
const DEFAULT_RESOLUTION = 128; // Start conservative; scale up with GPU later
const DEFAULT_WAVELENGTH_CHANNELS = 8; // Fewer channels for CPU FDTD performance

/**
 * Manages field grid allocation within a memory budget.
 * Automatically culls low-priority grids when budget is exceeded.
 */
export class FieldGridManager {
  private activeGrids: Map<string, { grid: FieldGrid; score: number }> = new Map();
  private pool: FieldGridPool;
  private memoryBudget: number;
  private currentMemoryUsage: number = 0;
  private resolution: number;
  private wavelengthChannels: number;

  constructor(
    memoryBudget: number = DEFAULT_MEMORY_BUDGET,
    resolution: number = DEFAULT_RESOLUTION,
    wavelengthChannels: number = DEFAULT_WAVELENGTH_CHANNELS,
  ) {
    this.memoryBudget = memoryBudget;
    this.resolution = resolution;
    this.wavelengthChannels = wavelengthChannels;
    this.pool = new FieldGridPool();
  }

  /** Request a field grid for a region. Returns existing grid if one exists, or allocates new. */
  getOrAllocate(key: string, bounds: FieldGridBounds, score: number): FieldGrid | null {
    const existing = this.activeGrids.get(key);
    if (existing) {
      existing.score = score; // update priority
      return existing.grid;
    }

    return this.allocate(key, bounds, score);
  }

  private allocate(key: string, bounds: FieldGridBounds, score: number): FieldGrid | null {
    const grid = this.pool.acquire(bounds, this.resolution, this.wavelengthChannels);
    const memNeeded = grid.memoryBytes;

    // Free space if over budget
    while (this.currentMemoryUsage + memNeeded > this.memoryBudget && this.activeGrids.size > 0) {
      this.cullLowestPriority();
    }

    if (this.currentMemoryUsage + memNeeded > this.memoryBudget) {
      this.pool.release(grid);
      return null; // Cannot fit even after culling
    }

    this.activeGrids.set(key, { grid, score });
    this.currentMemoryUsage += memNeeded;
    return grid;
  }

  /** Release a specific grid */
  release(key: string): void {
    const entry = this.activeGrids.get(key);
    if (entry) {
      this.currentMemoryUsage -= entry.grid.memoryBytes;
      this.pool.release(entry.grid);
      this.activeGrids.delete(key);
    }
  }

  /** Release all grids whose keys are not in the active set */
  retainOnly(activeKeys: Set<string>): void {
    for (const key of this.activeGrids.keys()) {
      if (!activeKeys.has(key)) {
        this.release(key);
      }
    }
  }

  private cullLowestPriority(): void {
    let lowestKey: string | null = null;
    let lowestScore = Infinity;

    for (const [key, entry] of this.activeGrids) {
      if (entry.score < lowestScore) {
        lowestScore = entry.score;
        lowestKey = key;
      }
    }

    if (lowestKey) {
      this.release(lowestKey);
    }
  }

  getGrid(key: string): FieldGrid | undefined {
    return this.activeGrids.get(key)?.grid;
  }

  getAllGrids(): FieldGrid[] {
    return Array.from(this.activeGrids.values()).map(e => e.grid);
  }

  get usedMemoryBytes(): number {
    return this.currentMemoryUsage;
  }

  get gridCount(): number {
    return this.activeGrids.size;
  }
}
